import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import moment from 'moment';
import MobileHeader from '../components/MobileHeader';

const OnlineExamResultScreen = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [answerDetails, setAnswerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/school-common/online-exam/get-exam-result-student', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setItems(response.data || []);
    } catch (exception) {
      console.error('Exception in loadData:', exception);
      setError('An unexpected error occurred while loading exam results.');
      handleCatch(exception);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const loadResultDetails = async (performanceID) => {
    try {
      setLoadingDetails(true);
      setDetailsError(null);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school-common/online-exam/get-question-answer-report?performanceID=${performanceID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setAnswerDetails(response.data || []);
    } catch (exception) {
      console.error('Exception in loadResultDetails:', exception);
      setDetailsError('Could not load exam details.');
      handleCatch(exception);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetails = (item) => {
    setSelectedItemId(item.id);
    setDetailsDialogOpen(true);
    setAnswerDetails(null);
    loadResultDetails(item.id);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setAnswerDetails(null);
    setDetailsError(null);
    setSelectedItemId(null);
  };

  const onRefresh = async () => {
    await loadData();
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader
        title="Exam Results"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 1, pb: 2 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>
              Loading results...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10, px: 3 }}>
            <Typography sx={{ fontSize: 13, color: '#d32f2f', textAlign: 'center', mb: 2 }}>{error}</Typography>
            <Button variant="contained" onClick={loadData}>Retry</Button>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10, px: 3 }}>
            <Typography sx={{ fontSize: 64, mb: 1.5 }}></Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: 17, mb: 1, textAlign: 'center' }}>
              No Exam Results
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', mb: 3 }}>
              Complete your exams to see results here.
            </Typography>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh}>
              Refresh
            </Button>
          </Box>
        ) : (
          items.map((item, index) => {
            const percentage =
              item.totalMarks > 0
                ? Math.round((item.totalMarksObtained / item.totalMarks) * 100)
                : 0;
            const passed = percentage >= 33; // fallback pass mark
            return (
              <Box
                key={index}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  mx: 0.5,
                  mt: 1,
                  overflow: 'hidden',
                }}
              >
                {/* Header */}
                <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: 14, color: '#222', flex: 1, mr: 1 }}>
                    {item.subjectName}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#888' }}>
                    {moment(item.startTime).format('DD MMM YYYY')}
                  </Typography>
                </Box>

                {/* Stats row */}
                <Box
                  sx={{
                    display: 'flex',
                    mx: 2,
                    mt: 1,
                    mb: 1,
                    bgcolor: '#F5F7FA',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ flex: 1, py: 1, textAlign: 'center', borderRight: '1px solid #E0E4EA' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: '#333' }}>
                      {item.totalCorrectAnswer}/{item.totalQuestion}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#888' }}>Correct</Typography>
                  </Box>
                  <Box sx={{ flex: 1, py: 1, textAlign: 'center', borderRight: '1px solid #E0E4EA' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: '#333' }}>
                      {item.totalMarksObtained}/{item.totalMarks}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#888' }}>Marks</Typography>
                  </Box>
                  <Box sx={{ flex: 1, py: 1, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: passed ? '#4CAF50' : '#f44336' }}>
                      {percentage}%
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#888' }}>Score</Typography>
                  </Box>
                </Box>

                <Divider />
                <Box sx={{ px: 2, py: 1.2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewDetails(item)}
                    sx={{ borderRadius: 20, textTransform: 'none', fontSize: 13 }}
                  >
                    View Detailed Results
                  </Button>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, m: 1, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>Result Details</Typography>
          <IconButton onClick={handleCloseDetails} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailsError ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: 13, color: '#d32f2f', mb: 2 }}>{detailsError}</Typography>
              <Button variant="contained" size="small" onClick={() => selectedItemId && loadResultDetails(selectedItemId)}>
                Retry
              </Button>
            </Box>
          ) : answerDetails && answerDetails.length > 0 ? (
            answerDetails.map((qItem, index) => {
              const isCorrect = qItem.answer === qItem.studentAnswer;
              return (
                <Box
                  key={index}
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: 2,
                    border: `1px solid ${isCorrect ? '#c8e6c9' : '#ffcdd2'}`,
                    mb: 1.5,
                    overflow: 'hidden',
                  }}
                >
                  {/* Question header */}
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: isCorrect ? '#E8F5E9' : '#FFEBEE',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold', fontSize: 13 }}>
                      Q{index + 1}
                    </Typography>
                    <Chip
                      icon={isCorrect ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                      label={isCorrect ? 'Correct' : 'Incorrect'}
                      size="small"
                      color={isCorrect ? 'success' : 'error'}
                      sx={{ fontSize: 11 }}
                    />
                  </Box>
                  {/* Question body */}
                  <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Box sx={{ fontSize: 13 }} dangerouslySetInnerHTML={{ __html: qItem.questionName }} />
                  </Box>
                  {/* Answer summary */}
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#fafafa', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: '#28a745', fontWeight: 600 }}>
                      Correct: Option {qItem.answer}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: isCorrect ? '#28a745' : '#dc3545', fontWeight: 600 }}>
                      Yours: {qItem.studentAnswer ? `Option ${qItem.studentAnswer}` : 'Not answered'}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography sx={{ textAlign: 'center', py: 4, fontSize: 14, color: '#888' }}>
              No details available
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={handleCloseDetails} variant="outlined" size="small" sx={{ borderRadius: 20 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnlineExamResultScreen;
